/* src/components/admin/UpdateUserModal.tsx */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { adminUserService, UpdateUserData, AdminUser } from '../../services/admin.user.service';
import { errorService } from '../../services/error.service';
import useForm, { ValidationRules } from '../../hooks/useForm';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSave, 
    faUser, 
    faEnvelope, 
    faLock, 
    faShieldAlt,
    faCheck,
    faEye,
    faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import styles from '../../styles/components/UpdateUserModal.module.css';
import modalStyles from '../../styles/common/Modal.module.css';

interface UpdateUserModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
    roles: { id: number; roleName: string }[];
    user: AdminUser | null;
}

const UpdateUserModal: React.FC<UpdateUserModalProps> = ({ show, onHide, onSuccess, roles, user }) => {
    const initialValues = useMemo<UpdateUserData>(() => ({
        idUser: user?.id || 0,
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        password: '', // Password will be empty initially for security
        idRoles: user?.roles.map(r => r.id) || []
    }), [user]);

    const validationRules = useMemo<ValidationRules<UpdateUserData>>(() => ({
        firstName: (val) => (!val?.trim() ? 'El nombre es obligatorio' : null),
        lastName: (val) => (!val?.trim() ? 'El apellido es obligatorio' : null),
        password: (val) => {
            // Password is now optional in updates. Only validate if not empty.
            if (!val?.trim()) return null;
            if (val.length < 8 || val.length > 16) return 'Debe tener entre 8 y 16 caracteres';
            return null;
        },
        idRoles: (val) => (val?.length === 0 ? 'Debes seleccionar al menos un rol' : null)
    }), []);

    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        setValues,
        resetForm
    } = useForm<UpdateUserData>(initialValues, validationRules);

    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Update form when user prop changes
    useEffect(() => {
        if (show && user) {
            setValues({
                idUser: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                password: '',
                idRoles: user.roles.map(r => r.id)
            });
        }
    }, [show, user, setValues]);

    const toggleRole = (roleId: number) => {
        const isSelected = values.idRoles.includes(roleId);
        const newRoles = isSelected
            ? values.idRoles.filter(id => id !== roleId)
            : [...values.idRoles, roleId];
            
        setValues(prev => ({ ...prev, idRoles: newRoles }));
    };

    const onSubmit = async () => {
        try {
            setLoading(true);
            const response = await adminUserService.updateUserData(values);

            if (response.statusCode === 200) {
                onHide();
                onSuccess();
                    await Swal.fire({
                        icon: 'success',
                        title: '¡Actualizado!',
                        text: 'Los datos del usuario han sido actualizados correctamente.',
                        confirmButtonText: 'Aceptar',
                        confirmButtonColor: 'var(--elPaket-primary-color, #e84d30)'
                    });
            } else {
                errorService.showErrorAlert(response, 'Error al Actualizar Usuario');
            }
        } catch (error: any) {
            console.error('Error in UpdateUserModal:', error);
            errorService.showErrorAlert(error, 'Error de Conexión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className={styles.updateUserModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Editar Usuario
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className={styles.modalSubtitle}>
                    Modifica la información del administrador seleccionado.
                </p>
                <Form onSubmit={handleSubmit(onSubmit)} id="update-user-form" noValidate>
                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Nombre</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="firstName"
                                    placeholder="Nombre"
                                    value={values.firstName}
                                    onChange={handleChange}
                                    className={`${styles.updateUserModalInput} ${errors.firstName ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.firstName && <div className={styles.errorText}>{errors.firstName}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Apellido</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="lastName"
                                    placeholder="Apellido"
                                    value={values.lastName}
                                    onChange={handleChange}
                                    className={`${styles.updateUserModalInput} ${errors.lastName ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.lastName && <div className={styles.errorText}>{errors.lastName}</div>}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Correo Electrónico (No editable)</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
                                <Form.Control
                                    type="email"
                                    value={user?.email || ''}
                                    className={styles.updateUserModalInput}
                                    disabled={true}
                                />
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Contraseña</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="8 a 16 caracteres"
                                    value={values.password}
                                    onChange={handleChange}
                                    className={`${styles.updateUserModalInput} ${errors.password ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className={styles.passwordToggle}
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </button>
                            </div>
                            {errors.password && <div className={styles.errorText}>{errors.password}</div>}
                        </Form.Group>
                    </div>
                    <div className={styles.rolesSection}>
                        <Form.Label className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faShieldAlt} className="me-2" style={{ color: 'var(--brand-black)' }} />
                            Asignar Roles
                        </Form.Label>
                        <div className={styles.rolesGrid}>
                            {roles.map(role => (
                                <div 
                                    key={role.id} 
                                    className={`${styles.roleItem} ${values.idRoles.includes(role.id) ? styles.roleItemActive : ''} ${errors.idRoles ? styles.roleError : ''}`}
                                    onClick={() => toggleRole(role.id)}
                                >
                                    <div className={styles.roleCheckbox}>
                                        {values.idRoles.includes(role.id) && <FontAwesomeIcon icon={faCheck} />}
                                    </div>
                                    <span>{role.roleName}</span>
                                </div>
                            ))}
                        </div>
                        {errors.idRoles && <div className={styles.errorText}>{errors.idRoles}</div>}
                    </div>
                </Form>
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                <Button 
                    variant="secondary" 
                    onClick={onHide} 
                    className={styles.secondaryBtn}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    type="submit" 
                    form="update-user-form"
                    className={modalStyles.closeBtn}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Actualizando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="me-2" /> Actualizar Datos
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateUserModal;
